<script>
import LandLeaseExceptionService from "@/services/LandLeaseExceptionService";
import { EventBus } from "@/libraries/EventBus";
import { ExceptionChangeType } from "@/constants/LandLease.const";
import dayjs from "dayjs";

export default {
    props: {
        landLease: { type: Object, required: true },
        initAddForm: { type: Boolean, default: false }
    },
    data() {
        return {
            showAddForm: this.initAddForm,
            editing: false,
            saving: false,
            reloading: false,
            newException: {
                id: null,
                land_lease_id: this.landLease.id,
                pt_major: null,
                pt_minor: null,
                standard_text: null,
                agreed_text: null,
                change_type: null,
                reason_notes: null
            }
        };
    },
    computed: {
        changeTypes() {
            return ExceptionChangeType;
        },
        exceptions() {
            return this.landLease.land_lease_exceptions || [];
        }
    },
    methods: {
        formatPoint(exc) {
            if (exc.pt_minor) {
                return `${exc.pt_major}.${exc.pt_minor}`;
            }
            return `${exc.pt_major}`;
        },
        badgeType(exc) {
            return exc.change_type == 2 ? "danger" : "warning";
        },
        resetForm() {
            this.editing = false;
            this.newException = {
                id: null,
                land_lease_id: this.landLease.id,
                pt_major: null,
                pt_minor: null,
                standard_text: null,
                agreed_text: null,
                change_type: null,
                reason_notes: null
            };
        },
        editException(exc) {
            this.editing = true;
            this.newException = {
                id: exc.id,
                land_lease_id: exc.land_lease_id,
                pt_major: exc.pt_major,
                pt_minor: exc.pt_minor,
                standard_text: exc.standard_text,
                agreed_text: exc.agreed_text,
                change_type: exc.change_type,
                reason_notes: exc.reason_notes
            };
            this.showAddForm = true;
        },
        async saveException() {
            this.saving = true;
            try {
                if (this.editing) {
                    await LandLeaseExceptionService.PUT(this.newException);
                    this.$notify({
                        message: this.$t(
                            "land_leases.notify_deviation_updated"
                        ),
                        timeout: 5000,
                        icon: "far fa-check",
                        type: "success"
                    });
                } else {
                    await LandLeaseExceptionService.POST(this.newException);
                    this.$notify({
                        message: this.$t(
                            "land_leases.notify_deviation_created"
                        ),
                        timeout: 5000,
                        icon: "far fa-check",
                        type: "success"
                    });
                }
                this.showAddForm = false;
                this.resetForm();
                this.reloading = true;
                EventBus.$emit("land-lease-exceptions-changed");
            } catch (e) {
                console.log("saveException e", e);
            }
            this.saving = false;
        },
        deleteException(exc) {
            this.$swal({
                icon: "warning",
                text: this.$t("land_leases.confirm_delete_deviation"),
                showConfirmButton: true,
                showCancelButton: true
            }).then(async confirmed => {
                if (confirmed.isConfirmed) {
                    try {
                        await LandLeaseExceptionService.DELETE(exc.id);
                        this.$notify({
                            message: this.$t(
                                "land_leases.notify_deviation_deleted"
                            ),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                        this.reloading = true;
                        EventBus.$emit("land-lease-exceptions-changed");
                    } catch (e) {
                        console.log("deleteException e", e);
                    }
                }
            });
        },
        downloadPDF() {
            const url = `/v1/land-leases/${this.landLease.id}/exceptions-report`;
            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Failed to fetch PDF");
                    }
                    return response.blob();
                })
                .then(blob => {
                    const link = document.createElement("a");
                    const now = dayjs().format("DD-MM-YYYY");
                    link.href = URL.createObjectURL(blob);
                    link.download = `afvigelser-${this.landLease.id}-${now}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                })
                .catch(e => {
                    console.log("downloadPDF e", e);
                });
        }
    },
    watch: {
        "landLease.land_lease_exceptions"() {
            this.reloading = false;
        }
    }
};
</script>

<template>
    <modal
        size="full"
        :show="true"
        @close="$emit('close')"
        class="budget-modal"
    >
        <template slot="header">
            {{ $t("land_leases.deviations") }}
        </template>

        <template slot="default">
            <!-- Header with add button -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 class="mb-0">
                    {{ $t("land_leases.deviations_overview") }}
                </h4>
                <base-button
                    size="sm"
                    type="primary"
                    outline
                    @click="
                        resetForm();
                        showAddForm = !showAddForm;
                    "
                >
                    <i class="far fa-plus"></i>
                    {{ $t("land_leases.add_deviation") }}
                </base-button>
            </div>

            <!-- Add form -->
            <div v-if="showAddForm" class="border rounded p-3 mb-3 bg-lighter">
                <FormulateForm @submit="saveException" v-model="newException">
                    <div class="row">
                        <div class="col-6 col-md-3">
                            <FormulateInput
                                name="pt_major"
                                type="number"
                                :label="$t('land_leases.pt_major')"
                                :placeholder="
                                    $t('land_leases.placeholder_pt_major')
                                "
                                validation="required"
                                :validation-name="$t('land_leases.pt_major')"
                            />
                        </div>
                        <div class="col-6 col-md-3">
                            <FormulateInput
                                name="pt_minor"
                                type="number"
                                :label="$t('land_leases.pt_minor')"
                                :placeholder="
                                    $t('land_leases.placeholder_pt_minor')
                                "
                            />
                        </div>
                        <div class="col-12 col-md-6">
                            <FormulateInput
                                name="change_type"
                                type="select"
                                :options="changeTypes"
                                :label="$t('land_leases.change_type')"
                                :placeholder="
                                    $t('land_leases.placeholder_change_type')
                                "
                                validation="required"
                                :validation-name="$t('land_leases.change_type')"
                            />
                        </div>
                        <div class="col-12 col-md-6">
                            <FormulateInput
                                name="standard_text"
                                type="textarea"
                                :label="$t('land_leases.standard_text')"
                                :placeholder="
                                    $t('land_leases.placeholder_standard_text')
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.standard_text')
                                "
                                rows="3"
                            />
                        </div>
                        <div class="col-12 col-md-6">
                            <FormulateInput
                                name="agreed_text"
                                type="textarea"
                                :label="$t('land_leases.agreed_text')"
                                :placeholder="
                                    $t('land_leases.placeholder_agreed_text')
                                "
                                validation="required"
                                :validation-name="$t('land_leases.agreed_text')"
                                rows="3"
                            />
                        </div>
                        <div class="col-12">
                            <FormulateInput
                                name="reason_notes"
                                type="textarea"
                                :label="$t('land_leases.reason_notes')"
                                :placeholder="
                                    $t('land_leases.placeholder_reason_notes')
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.reason_notes')
                                "
                                rows="2"
                            />
                        </div>
                    </div>
                    <div class="text-right">
                        <base-button
                            size="sm"
                            type="secondary"
                            outline
                            @click="
                                showAddForm = false;
                                resetForm();
                            "
                        >
                            {{ $t("cancel") }}
                        </base-button>
                        <base-button
                            size="sm"
                            type="primary"
                            :loading="saving"
                            native-type="submit"
                        >
                            {{ $t("save") }}
                        </base-button>
                    </div>
                </FormulateForm>
            </div>

            <!-- Deviations table -->
            <v-loader :loading="reloading" v-if="reloading" />
            <template v-else>
                <div v-if="exceptions.length" class="table-responsive">
                    <table class="table align-items-center">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t("land_leases.hdr_point") }}</th>
                                <th>{{ $t("land_leases.standard_text") }}</th>
                                <th>{{ $t("land_leases.agreed_text") }}</th>
                                <th>{{ $t("land_leases.change_type") }}</th>
                                <th>{{ $t("land_leases.reason_notes") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="exc in exceptions" :key="exc.id">
                                <td class="text-nowrap">
                                    {{ formatPoint(exc) }}
                                </td>
                                <td class="small">
                                    {{ exc.standard_text }}
                                </td>
                                <td class="small">
                                    {{ exc.agreed_text }}
                                </td>
                                <td>
                                    <badge :type="badgeType(exc)">
                                        {{ exc.change_type_text }}
                                    </badge>
                                </td>
                                <td class="small">
                                    {{ exc.reason_notes }}
                                </td>
                                <td class="text-right text-nowrap">
                                    <base-button
                                        size="sm"
                                        type="primary"
                                        outline
                                        @click="editException(exc)"
                                    >
                                        <i class="far fa-pencil"></i>
                                    </base-button>
                                    <base-button
                                        size="sm"
                                        type="danger"
                                        outline
                                        @click="deleteException(exc)"
                                    >
                                        <i class="far fa-trash"></i>
                                    </base-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="text-muted">
                    {{ $t("land_leases.no_deviations") }}
                </div>
            </template>

            <!-- Download PDF button -->
            <div class="mt-4" v-if="exceptions.length">
                <base-button
                    size="sm"
                    type="secondary"
                    outline
                    @click="downloadPDF"
                >
                    <i class="far fa-file-pdf fa-fw"></i>
                    {{ $t("land_leases.download_deviations_pdf") }}
                </base-button>
            </div>
        </template>
    </modal>
</template>
